import React, { useState, useEffect } from 'react';
import { systemAdminAPI } from '../services/api';
import { Shield, Plus, Calendar, DollarSign, AlertCircle, Trash2, Wifi, WifiOff, User, Clock, KeyRound, Globe, BellRing, ChevronDown, ChevronUp } from 'lucide-react';
import toast from 'react-hot-toast';

const SystemAdmin = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('superToken'));
  const [superAdminUsername, setSuperAdminUsername] = useState(localStorage.getItem('superUsername') || 'superadmin');
  const [companies, setCompanies] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(null);
  const [showSuperAdminModal, setShowSuperAdminModal] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(null);
  const [isCreatingCompany, setIsCreatingCompany] = useState(false);
  const [isSendingWarning, setIsSendingWarning] = useState(false);
  const [expandedCompanyId, setExpandedCompanyId] = useState(null);
  
  // Form states
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [companyForm, setCompanyForm] = useState({ 
    name: '', code: '', months: '12',
    owner_name: '', phone: '', email: '', 
    tax_office: '', tax_number: '', address: '',
    admin_username: 'admin', admin_password: ''
  });
  const [extendForm, setExtendForm] = useState({ amount: '', months: '12', notes: '' });
  const [passwordForm, setPasswordForm] = useState({ new_password: '', admin_username: 'admin' });
  const [warningForm, setWarningForm] = useState({ message: '' });
  const [superAdminForm, setSuperAdminForm] = useState({
    username: localStorage.getItem('superUsername') || 'superadmin',
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const emptyCompanyForm = {
    name: '', code: '', months: '12',
    owner_name: '', phone: '', email: '',
    tax_office: '', tax_number: '', address: '',
    admin_username: 'admin', admin_password: ''
  };

  useEffect(() => {
    if (isLoggedIn) fetchCompanies();
  }, [isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn) fetchSuperAdminProfile();
  }, [isLoggedIn]);

  const fetchCompanies = async () => {
    try {
      const res = await systemAdminAPI.getCompanies();
      setCompanies(res.data);
    } catch (err) {
      if ([401, 403].includes(err.response?.status)) {
        localStorage.removeItem('superToken');
        setIsLoggedIn(false);
      }
      toast.error('Firmalar yüklenemedi');
    }
  };

  const fetchSuperAdminProfile = async () => {
    try {
      const res = await systemAdminAPI.getProfile();
      const username = res.data.username || 'superadmin';
      setSuperAdminUsername(username);
      localStorage.setItem('superUsername', username);
      setSuperAdminForm((prev) => ({ ...prev, username }));
    } catch (err) {
      if ([401, 403].includes(err.response?.status)) {
        localStorage.removeItem('superToken');
        localStorage.removeItem('superUsername');
        setIsLoggedIn(false);
      }
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await systemAdminAPI.login(loginForm);
      localStorage.setItem('superToken', res.data.token);
      localStorage.setItem('superUsername', res.data.username);
      setSuperAdminUsername(res.data.username);
      setSuperAdminForm((prev) => ({ ...prev, username: res.data.username }));
      setIsLoggedIn(true);
      toast.success('Sistem yöneticisi girişi başarılı');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Giriş başarısız');
    }
  };

  const handleSuperAdminUpdate = async (e) => {
    e.preventDefault();

    if (superAdminForm.new_password && superAdminForm.new_password !== superAdminForm.confirm_password) {
      toast.error('Yeni şifreler eşleşmiyor');
      return;
    }

    try {
      const payload = {
        username: superAdminForm.username,
        current_password: superAdminForm.current_password,
      };

      if (superAdminForm.new_password) {
        payload.new_password = superAdminForm.new_password;
      }

      const res = await systemAdminAPI.updateProfile(payload);
      localStorage.setItem('superToken', res.data.token);
      localStorage.setItem('superUsername', res.data.username);
      setSuperAdminUsername(res.data.username);
      setShowSuperAdminModal(false);
      setSuperAdminForm({
        username: res.data.username,
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      toast.success('Sistem yöneticisi bilgileri güncellendi');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Bilgiler güncellenemedi');
    }
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();

    const normalizedCode = companyForm.code.trim().toLowerCase();

    if (!normalizedCode) {
      toast.error('Firma kodu zorunludur');
      return;
    }

    if (companies.some((company) => String(company.code || '').trim().toLowerCase() === normalizedCode)) {
      toast.error('Bu firma kodu zaten kullanılıyor');
      return;
    }

    setIsCreatingCompany(true);

    try {
      await systemAdminAPI.createCompany({
        ...companyForm,
        code: companyForm.code.trim(),
        name: companyForm.name.trim(),
        admin_username: companyForm.admin_username.trim(),
        license_end_months: companyForm.months
      });
      toast.success('Firma oluşturuldu');
      setShowAddModal(false);
      setCompanyForm(emptyCompanyForm);
      fetchCompanies();
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Hata oluştu';
      toast.error(errorMsg);
    } finally {
      setIsCreatingCompany(false);
    }
  };

  const handleExtendLicense = async (e) => {
    e.preventDefault();
    try {
      await systemAdminAPI.extendLicense(showExtendModal.id, extendForm);
      toast.success('Lisans uzatıldı');
      setShowExtendModal(null);
      fetchCompanies();
    } catch (err) {
      toast.error('Hata oluştu');
    }
  };

  const handleStatusUpdate = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      await systemAdminAPI.updateCompanyStatus(id, { is_active: newStatus });
      toast.success(newStatus === 1 ? 'Firma aktif edildi' : 'Firma askıya alındı');
      fetchCompanies();
    } catch (err) {
      toast.error('Durum güncellenemedi');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    try {
      await systemAdminAPI.resetCompanyPassword(showPasswordModal.id, passwordForm);
      toast.success('Admin şifresi güncellendi');
      setShowPasswordModal(null);
      setPasswordForm({ new_password: '', admin_username: 'admin' });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Hata oluştu');
    }
  };

  const handleDeleteCompany = async (company) => {
    if (window.confirm(`${company.name} firmasını ve TÜM verilerini (veritabanı dahil) kalıcı olarak silmek istediğinize emin misiniz? Bu işlem geri alınamaz!`)) {
      try {
        await systemAdminAPI.deleteCompany(company.id);
        toast.success('Firma başarıyla silindi');
        fetchCompanies();
      } catch (err) {
        toast.error(err.response?.data?.error || 'Firma silinemedi');
      }
    }
  };

  const handleSendWarning = async (e) => {
    e.preventDefault();
    if (!showWarningModal) return;

    setIsSendingWarning(true);
    try {
      const res = await systemAdminAPI.sendLicenseWarning(showWarningModal.id, warningForm);
      toast.success(res.data?.message || 'Uyarı gönderildi');
      setShowWarningModal(null);
      setWarningForm({ message: '' });
      fetchCompanies();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Uyarı gönderilemedi');
    } finally {
      setIsSendingWarning(false);
    }
  };

  const handleSuperAdminModalOpen = () => {
    setSuperAdminForm((prev) => ({
      ...prev,
      username: superAdminUsername,
      current_password: '',
      new_password: '',
      confirm_password: ''
    }));
    setShowSuperAdminModal(true);
  };

  const toggleCompanyDetails = (companyId) => {
    setExpandedCompanyId((current) => current === companyId ? null : companyId);
  };

  const formatDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('tr-TR');
  };

  const getActivityStatus = (company) => {
    if (company.activity?.is_online) return { label: 'Online', className: 'bg-emerald-100 text-emerald-700' };
    if (company.activity?.last_seen_at) return { label: 'Offline', className: 'bg-slate-100 text-slate-700' };
    return { label: 'Baglanti Yok', className: 'bg-amber-100 text-amber-700' };
  };

  const getReminderStatus = (company) => {
    if (company.days_remaining == null) return { label: 'Belirsiz', className: 'bg-slate-100 text-slate-700' };
    if (company.days_remaining < 0) return { label: 'Suresi Doldu', className: 'bg-red-100 text-red-700' };
    if (company.days_remaining <= 30) return { label: 'Uyari Donemi', className: 'bg-amber-100 text-amber-700' };
    return { label: 'Normal', className: 'bg-emerald-100 text-emerald-700' };
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <Shield className="w-16 h-16 text-blue-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-slate-900">Sistem Yönetimi</h1>
            <p className="text-slate-500">Sadece yetkili personel girişi</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="text"
              placeholder="Kullanıcı Adı"
              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              value={loginForm.username}
              onChange={(e) => setLoginForm({...loginForm, username: e.target.value})}
            />
            <input
              type="password"
              placeholder="Şifre"
              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              value={loginForm.password}
              onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
            />
            <button className="w-full bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 transition">
              Giriş Yap
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Lisans ve Firma Yönetimi</h1>
            <p className="text-slate-500">Tüm kayıtlı firmalar ve lisans durumları</p>
            <p className="text-xs text-slate-400 mt-1">Sistem yöneticisi: {superAdminUsername}</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleSuperAdminModalOpen}
              className="flex items-center gap-2 bg-slate-800 text-white px-4 py-3 rounded-xl font-bold hover:bg-slate-700 transition"
            >
              <Shield size={18} /> Superadmin Ayarlari
            </button>
            <button 
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-blue-700 transition"
            >
              <Plus size={20} /> Yeni Firma Ekle
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6">
          {companies.map(company => {
            const isExpired = new Date(company.license_end) < new Date();
            const activityStatus = getActivityStatus(company);
            const reminderStatus = getReminderStatus(company);
            const isExpanded = expandedCompanyId === company.id;
            return (
              <div key={company.id} className="bg-white rounded-2xl shadow-sm border p-5">
                <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                  <div className="flex items-start gap-4">
                    <div className={`mt-1 flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${isExpired ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
                      {isExpired ? <AlertCircle /> : <Shield />}
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-lg font-bold text-slate-900">{company.name}</h3>
                        <span className={`inline-flex rounded-full px-2 py-1 text-[11px] font-bold ${activityStatus.className}`}>
                          {activityStatus.label}
                        </span>
                        <span className={`inline-flex rounded-full px-2 py-1 text-[11px] font-bold ${reminderStatus.className}`}>
                          {reminderStatus.label}
                        </span>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-slate-500">
                        <span className="bg-slate-100 px-2 py-1 rounded">Kod: {company.code}</span>
                        <span className="flex items-center gap-1"><Calendar size={14} /> Bitiş: {new Date(company.license_end).toLocaleDateString('tr-TR')}</span>
                        <span className="flex items-center gap-1">
                          {company.activity?.is_online ? <Wifi size={14} /> : <WifiOff size={14} />}
                          {company.activity?.active_user_count || 0} aktif kullanıcı
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3 xl:items-end">
                    <div className="flex flex-wrap items-center gap-3 xl:justify-end">
                      <div className="text-left xl:text-right">
                        <p className="text-xs text-slate-400 uppercase font-bold">Durum</p>
                        <p className={`font-bold ${isExpired ? 'text-red-600' : (company.is_active === 0 ? 'text-orange-600' : 'text-green-600')}`}>
                          {isExpired ? 'Süresi Doldu' : (company.is_active === 0 ? 'Askıya Alındı' : 'Aktif')}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleCompanyDetails(company.id)}
                        className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50"
                      >
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        {isExpanded ? 'Detayi Daralt' : 'Detayi Ac'}
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2 xl:justify-end">
                      <button 
                        onClick={() => setShowExtendModal(company)}
                        className="bg-slate-900 text-white px-3 py-2 rounded-lg hover:bg-slate-800 transition text-xs font-bold"
                      >
                        Süre Uzat
                      </button>
                      <button 
                        onClick={() => setShowPasswordModal(company)}
                        className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition text-xs font-bold"
                      >
                        Şifre Sıfırla
                      </button>
                      <button
                        onClick={() => setShowWarningModal(company)}
                        className="bg-amber-500 text-white px-3 py-2 rounded-lg hover:bg-amber-600 transition text-xs font-bold"
                      >
                        Uyarı Gönder
                      </button>
                      <button 
                        onClick={() => handleStatusUpdate(company.id, company.is_active)}
                        className={`${company.is_active === 0 ? 'bg-green-600' : 'bg-red-600'} text-white px-3 py-2 rounded-lg hover:opacity-90 transition text-xs font-bold`}
                      >
                        {company.is_active === 0 ? 'Ban Kaldır' : 'Banla'}
                      </button>
                      <button 
                        onClick={() => handleDeleteCompany(company)}
                        className="bg-red-100 text-red-600 px-3 py-2 rounded-lg hover:bg-red-200 transition text-xs font-bold flex items-center gap-1"
                        title="Firmayı Kalıcı Olarak Sil"
                      >
                        <Trash2 size={14} /> Sil
                      </button>
                    </div>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3 border-t pt-4 text-sm md:grid-cols-3 xl:grid-cols-6">
                  <div>
                    <p className="text-slate-400 font-bold uppercase text-[10px]">Yetkili</p>
                    <p className="font-semibold text-slate-700">{company.owner_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 font-bold uppercase text-[10px]">Telefon</p>
                    <p className="font-semibold text-slate-700">{company.phone || '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 font-bold uppercase text-[10px]">E-posta</p>
                    <p className="font-semibold text-slate-700">{company.email || '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 font-bold uppercase text-[10px]">Kalan Gün</p>
                    <p className="font-semibold text-slate-700">{company.days_remaining ?? '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 font-bold uppercase text-[10px]">Son Giriş</p>
                    <p className="font-semibold text-slate-700">{formatDateTime(company.activity?.last_login_at)}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 font-bold uppercase text-[10px]">Son IP</p>
                    <p className="font-semibold text-slate-700">{company.activity?.last_ip || '-'}</p>
                  </div>
                </div>

                {isExpanded && (
                  <>
                    <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-slate-400 font-bold uppercase text-[10px]">Yetkili</p>
                        <p className="font-semibold text-slate-700">{company.owner_name || '-'}</p>
                      </div>
                      <div>
                        <p className="text-slate-400 font-bold uppercase text-[10px]">Telefon</p>
                        <p className="font-semibold text-slate-700">{company.phone || '-'}</p>
                      </div>
                      <div>
                        <p className="text-slate-400 font-bold uppercase text-[10px]">Vergi No / Daire</p>
                        <p className="font-semibold text-slate-700">{company.tax_number || '-'} / {company.tax_office || '-'}</p>
                      </div>
                      <div>
                        <p className="text-slate-400 font-bold uppercase text-[10px]">E-posta</p>
                        <p className="font-semibold text-slate-700">{company.email || '-'}</p>
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                      <div className="rounded-xl border bg-slate-50 p-4">
                        <div className="flex items-center gap-2 text-slate-500 mb-2">
                          <User size={16} />
                          <p className="text-xs font-bold uppercase">Admin Hesabı</p>
                        </div>
                        <p className="text-sm font-semibold text-slate-800">Kullanıcı: {company.admin_username || '-'}</p>
                        <p className="text-sm text-slate-600 mt-1">E-posta: {company.admin_email || '-'}</p>
                        <p className="text-xs text-slate-500 mt-2">Oluşturulma: {formatDateTime(company.admin_created_at)}</p>
                      </div>

                      <div className="rounded-xl border bg-slate-50 p-4">
                        <div className="flex items-center gap-2 text-slate-500 mb-2">
                          <KeyRound size={16} />
                          <p className="text-xs font-bold uppercase">Şifre Bilgisi</p>
                        </div>
                        <p className="text-sm font-semibold text-slate-800">Görüntüleme: Kapalı</p>
                        <p className="text-xs text-slate-500 mt-2">{company.password_info}</p>
                      </div>

                      <div className="rounded-xl border bg-slate-50 p-4">
                        <div className="flex items-center gap-2 text-slate-500 mb-2">
                          <Globe size={16} />
                          <p className="text-xs font-bold uppercase">Bağlantı Bilgisi</p>
                        </div>
                        <p className="text-sm font-semibold text-slate-800">Son IP: {company.activity?.last_ip || '-'}</p>
                        <p className="text-sm text-slate-600 mt-1">Son kullanıcı: {company.activity?.last_username || '-'}</p>
                        <p className="text-xs text-slate-500 mt-2">Rol: {company.activity?.last_role || '-'}</p>
                      </div>

                      <div className="rounded-xl border bg-slate-50 p-4">
                        <div className="flex items-center gap-2 text-slate-500 mb-2">
                          <Clock size={16} />
                          <p className="text-xs font-bold uppercase">Oturum Takibi</p>
                        </div>
                        <p className="text-sm font-semibold text-slate-800">Son giriş: {formatDateTime(company.activity?.last_login_at)}</p>
                        <p className="text-sm text-slate-600 mt-1">Son bağlantı: {formatDateTime(company.activity?.last_seen_at)}</p>
                        <p className="text-xs text-slate-500 mt-2">Online pencere: {company.activity?.online_window_minutes || 5} dk</p>
                      </div>

                      <div className="rounded-xl border bg-slate-50 p-4">
                        <div className="flex items-center gap-2 text-slate-500 mb-2">
                          {company.activity?.is_online ? <Wifi size={16} /> : <WifiOff size={16} />}
                          <p className="text-xs font-bold uppercase">Canlı Durum</p>
                        </div>
                        <p className="text-sm font-semibold text-slate-800">Aktif kullanıcı: {company.activity?.active_user_count || 0}</p>
                        <p className="text-sm text-slate-600 mt-1">Çevrimiçi: {company.activity?.is_online ? 'Evet' : 'Hayır'}</p>
                        <p className="text-xs text-slate-500 mt-2">Lisans: {company.license_status || 'active'}</p>
                      </div>

                      <div className="rounded-xl border bg-slate-50 p-4">
                        <div className="flex items-center gap-2 text-slate-500 mb-2">
                          <Shield size={16} />
                          <p className="text-xs font-bold uppercase">Firma Özeti</p>
                        </div>
                        <p className="text-sm font-semibold text-slate-800">Firma kodu: {company.code}</p>
                        <p className="text-sm text-slate-600 mt-1">Demo: {company.is_demo ? 'Evet' : 'Hayır'}</p>
                        <p className="text-xs text-slate-500 mt-2">Son ödeme: {formatDateTime(company.last_payment)}</p>
                      </div>

                      <div className="rounded-xl border bg-slate-50 p-4">
                        <div className="flex items-center gap-2 text-slate-500 mb-2">
                          <BellRing size={16} />
                          <p className="text-xs font-bold uppercase">Lisans Hatırlatma</p>
                        </div>
                        <div className={`inline-flex rounded-full px-2 py-1 text-[11px] font-bold ${reminderStatus.className}`}>
                          {reminderStatus.label}
                        </div>
                        <p className="text-sm font-semibold text-slate-800 mt-2">Kalan gün: {company.days_remaining ?? '-'}</p>
                        <p className="text-sm text-slate-600 mt-1">Son uyarı: {formatDateTime(company.reminders?.last_sent_at)}</p>
                        <p className="text-xs text-slate-500 mt-2">
                          Otomatik hatırlatma, bitime {company.reminders?.auto_window_days || 30} gün kala her {company.reminders?.auto_interval_days || 7} günde bir gönderilir.
                        </p>
                      </div>
                    </div>
                    {company.address && (
                      <div className="mt-4 pt-4 border-t">
                        <p className="text-slate-400 font-bold uppercase text-[10px]">Adres</p>
                        <p className="text-slate-600 text-xs">{company.address}</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {/* Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            <h2 className="text-2xl font-bold mb-2">Şifre Sıfırla</h2>
            <p className="text-slate-500 mb-6">{showPasswordModal.name} firmasının admin şifresini değiştir</p>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Admin Kullanıcı Adı</label>
                <input
                  placeholder="Kullanıcı Adı (Örn: admin)"
                  className="w-full px-4 py-3 border rounded-lg"
                  value={passwordForm.admin_username}
                  onChange={(e) => setPasswordForm({...passwordForm, admin_username: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Yeni Şifre</label>
                <input
                  type="text"
                  placeholder="Yeni Şifre"
                  className="w-full px-4 py-3 border rounded-lg font-mono"
                  required
                  onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                />
              </div>
              <div className="flex gap-4 mt-6">
                <button type="button" onClick={() => setShowPasswordModal(null)} className="flex-1 py-3 border rounded-lg">İptal</button>
                <button className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-bold">Şifreyi Güncelle</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-2xl p-8 max-w-2xl w-full my-8">
            <h2 className="text-2xl font-bold mb-6">Yeni Firma Kaydı</h2>
            <form onSubmit={handleCreateCompany} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Firma Adı *</label>
                  <input
                    placeholder="Firma Adı"
                    className="w-full px-4 py-2 border rounded-lg"
                    required
                    onChange={(e) => setCompanyForm({...companyForm, name: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Firma Kodu *</label>
                  <input
                    placeholder="Firma Kodu (benzersiz)"
                    className="w-full px-4 py-2 border rounded-lg"
                    required
                    onChange={(e) => setCompanyForm({...companyForm, code: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Yetkili Ad Soyad</label>
                  <input
                    placeholder="Yetkili Kişi"
                    className="w-full px-4 py-2 border rounded-lg"
                    onChange={(e) => setCompanyForm({...companyForm, owner_name: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Telefon</label>
                  <input
                    placeholder="05xx xxx xx xx"
                    className="w-full px-4 py-2 border rounded-lg"
                    onChange={(e) => setCompanyForm({...companyForm, phone: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">E-posta</label>
                  <input
                    type="email"
                    placeholder="firma@eposta.com"
                    className="w-full px-4 py-2 border rounded-lg"
                    onChange={(e) => setCompanyForm({...companyForm, email: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Lisans Süresi</label>
                  <select 
                    className="w-full px-4 py-2 border rounded-lg"
                    onChange={(e) => setCompanyForm({...companyForm, months: e.target.value})}
                  >
                    <option value="12">12 Ay (1 Yıl)</option>
                    <option value="24">24 Ay (2 Yıl)</option>
                    <option value="1">1 Ay</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Vergi Dairesi</label>
                  <input
                    placeholder="Vergi Dairesi"
                    className="w-full px-4 py-2 border rounded-lg"
                    onChange={(e) => setCompanyForm({...companyForm, tax_office: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Vergi No</label>
                  <input
                    placeholder="Vergi Numarası"
                    className="w-full px-4 py-2 border rounded-lg"
                    onChange={(e) => setCompanyForm({...companyForm, tax_number: e.target.value})}
                  />
                </div>
              </div>
              <div className="bg-blue-50 p-4 rounded-xl space-y-4">
                <h3 className="text-sm font-bold text-blue-800 flex items-center gap-2">
                  <Shield size={16} /> Firma Admin Hesabı Oluştur
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-blue-600 mb-1 uppercase">Admin Kullanıcı Adı *</label>
                    <input
                      placeholder="Kullanıcı Adı"
                      className="w-full px-4 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      required
                      value={companyForm.admin_username}
                      onChange={(e) => setCompanyForm({...companyForm, admin_username: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-blue-600 mb-1 uppercase">Admin Şifre *</label>
                    <input
                      type="text"
                      placeholder="Şifre"
                      className="w-full px-4 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      required
                      onChange={(e) => setCompanyForm({...companyForm, admin_password: e.target.value})}
                    />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Adres</label>
                <textarea
                  placeholder="Firma Adresi"
                  className="w-full px-4 py-2 border rounded-lg h-20"
                  onChange={(e) => setCompanyForm({...companyForm, address: e.target.value})}
                />
              </div>
              <div className="flex gap-4 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setCompanyForm(emptyCompanyForm);
                  }}
                  className="flex-1 py-3 border rounded-lg font-bold"
                  disabled={isCreatingCompany}
                >
                  İptal
                </button>
                <button
                  className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
                  disabled={isCreatingCompany}
                >
                  {isCreatingCompany ? 'Oluşturuluyor...' : 'Firma Oluştur'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Extend Modal */}
      {showExtendModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            <h2 className="text-2xl font-bold mb-2">Lisans Uzat</h2>
            <p className="text-slate-500 mb-6">{showExtendModal.name} için ödeme ve süre girişi</p>
            <form onSubmit={handleExtendLicense} className="space-y-4">
              <div className="relative">
                <DollarSign className="absolute left-3 top-3.5 text-slate-400" size={18} />
                <input
                  type="number"
                  placeholder="Ödeme Tutarı"
                  className="w-full pl-10 pr-4 py-3 border rounded-lg"
                  required
                  onChange={(e) => setExtendForm({...extendForm, amount: e.target.value})}
                />
              </div>
              <select 
                className="w-full px-4 py-3 border rounded-lg"
                onChange={(e) => setExtendForm({...extendForm, months: e.target.value})}
              >
                <option value="12">12 Ay Ekle</option>
                <option value="24">24 Ay Ekle</option>
                <option value="6">6 Ay Ekle</option>
                <option value="1">1 Ay Ekle</option>
              </select>
              <textarea
                placeholder="Notlar"
                className="w-full px-4 py-3 border rounded-lg h-24"
                onChange={(e) => setExtendForm({...extendForm, notes: e.target.value})}
              />
              <div className="flex gap-4 mt-6">
                <button type="button" onClick={() => setShowExtendModal(null)} className="flex-1 py-3 border rounded-lg">İptal</button>
                <button className="flex-1 py-3 bg-green-600 text-white rounded-lg font-bold">Ödemeyi Kaydet</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showWarningModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            <h2 className="text-2xl font-bold mb-2">Lisans Uyarısı Gönder</h2>
            <p className="text-slate-500 mb-6">
              {showWarningModal.name} firmasına manuel hatırlatma bildirimi gönder.
            </p>
            <form onSubmit={handleSendWarning} className="space-y-4">
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800">
                Bu bildirim firma kullanıcılarının bildirim alanına düşer.
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Ek Not</label>
                <textarea
                  placeholder="Isterseniz ozel bir not ekleyin"
                  className="w-full px-4 py-3 border rounded-lg h-28"
                  value={warningForm.message}
                  onChange={(e) => setWarningForm({ message: e.target.value })}
                />
              </div>
              <div className="flex gap-4 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowWarningModal(null);
                    setWarningForm({ message: '' });
                  }}
                  className="flex-1 py-3 border rounded-lg"
                  disabled={isSendingWarning}
                >
                  İptal
                </button>
                <button
                  className="flex-1 py-3 bg-amber-500 text-white rounded-lg font-bold disabled:opacity-60"
                  disabled={isSendingWarning}
                >
                  {isSendingWarning ? 'Gönderiliyor...' : 'Uyarıyı Gönder'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showSuperAdminModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            <h2 className="text-2xl font-bold mb-2">Superadmin Bilgileri</h2>
            <p className="text-slate-500 mb-6">Kullanıcı adı ve şifrenizi güvenli şekilde güncelleyin</p>
            <form onSubmit={handleSuperAdminUpdate} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Yeni Kullanıcı Adı</label>
                <input
                  className="w-full px-4 py-3 border rounded-lg"
                  value={superAdminForm.username}
                  onChange={(e) => setSuperAdminForm({ ...superAdminForm, username: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Mevcut Şifre</label>
                <input
                  type="password"
                  className="w-full px-4 py-3 border rounded-lg"
                  value={superAdminForm.current_password}
                  onChange={(e) => setSuperAdminForm({ ...superAdminForm, current_password: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Yeni Şifre</label>
                <input
                  type="password"
                  className="w-full px-4 py-3 border rounded-lg"
                  value={superAdminForm.new_password}
                  onChange={(e) => setSuperAdminForm({ ...superAdminForm, new_password: e.target.value })}
                  placeholder="Degistirmek istemiyorsaniz bos birakin"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Yeni Şifre Tekrar</label>
                <input
                  type="password"
                  className="w-full px-4 py-3 border rounded-lg"
                  value={superAdminForm.confirm_password}
                  onChange={(e) => setSuperAdminForm({ ...superAdminForm, confirm_password: e.target.value })}
                  placeholder="Yeni sifreyi tekrar girin"
                />
              </div>
              <div className="flex gap-4 mt-6">
                <button type="button" onClick={() => setShowSuperAdminModal(false)} className="flex-1 py-3 border rounded-lg">İptal</button>
                <button className="flex-1 py-3 bg-slate-900 text-white rounded-lg font-bold">Güncelle</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemAdmin;


